import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, Mail, Key, Shield, Eye, Edit, 
  Check, X, Clock, Send, Copy, AlertCircle, Sparkles, 
  PartyPopper, Lock, Unlock, Crown, Star, Gift, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import { api } from '../App';
import { toast } from 'sonner';

const MemberAccess = ({ user, setUser }) => {
  const [members, setMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCreateLogin, setShowCreateLogin] = useState(false);
  const [showCredentials, setShowCredentials] = useState(null); // For displaying generated credentials
  const [showEditRole, setShowEditRole] = useState(false);
  const [showEditCredentials, setShowEditCredentials] = useState(false);

  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', temp_password: '', role: 'viewer', scope: 'own' });
  const [roleForm, setRoleForm] = useState({ role: 'viewer', scope: 'own' });
  const [credentialsForm, setCredentialsForm] = useState({ email: '', new_password: '' });

  useEffect(() => {
    fetchMembers();
    fetchJoinRequests();
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await api.get('/members/with-access');
      setMembers(res.data);
    } catch (err) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const fetchJoinRequests = async () => {
    try {
      const res = await api.get('/members/join-requests');
      setJoinRequests(res.data);
    } catch (err) {
      console.error('Failed to load join requests');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setLoginForm({ ...loginForm, temp_password: password });
    toast.success('🎲 Password generated!');
  };

  const triggerConfetti = () => {
    // Simple celebration effect - just show success toast
    toast.success('🎊 Welcome to the family!');
  };

  const handleCreateLogin = async () => {
    if (!loginForm.email || !loginForm.temp_password) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const res = await api.post(`/members/${selectedMember.id}/create-login`, loginForm);
      triggerConfetti();
      toast.success('🎉 ' + res.data.message);
      setShowCreateLogin(false);
      // Show the credentials modal
      setShowCredentials({
        name: selectedMember.name,
        email: loginForm.email,
        password: loginForm.temp_password,
        role: loginForm.role,
        scope: loginForm.scope
      });
      setSelectedMember(null);
      setLoginForm({ email: '', temp_password: '', role: 'viewer', scope: 'own' });
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create login');
    }
  };

  const handleUpdateRole = async () => {
    try {
      const res = await api.put(`/members/${selectedMember.id}/role`, roleForm);
      toast.success('✨ ' + res.data.message);
      setShowEditRole(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleUpdateCredentials = async () => {
    if (!credentialsForm.email && !credentialsForm.new_password) {
      toast.error('Please provide email or new password to update');
      return;
    }
    try {
      const res = await api.put(`/members/${selectedMember.id}/credentials`, credentialsForm);
      toast.success('🔑 ' + res.data.message);
      
      // Show the new credentials if password was reset
      if (credentialsForm.new_password) {
        setShowEditCredentials(false);
        setShowCredentials({
          name: selectedMember.name,
          email: res.data.new_email || selectedMember.email,
          password: credentialsForm.new_password,
          role: selectedMember.role,
          scope: selectedMember.scope,
          isReset: true
        });
      } else {
        setShowEditCredentials(false);
      }
      
      setSelectedMember(null);
      setCredentialsForm({ email: '', new_password: '' });
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update credentials');
    }
  };

  const handleRevokeAccess = async () => {
    if (!window.confirm(`Are you sure you want to revoke access for ${selectedMember.name}? They will no longer be able to login.`)) {
      return;
    }
    try {
      await api.delete(`/members/${selectedMember.id}/access`);
      toast.success('🚫 Access revoked');
      setShowEditCredentials(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const handleApproveRequest = async (requestId, name) => {
    try {
      await api.post(`/members/join-requests/${requestId}/approve`);
      triggerConfetti();
      toast.success(`🎊 Welcome aboard, ${name}!`);
      fetchJoinRequests();
      fetchMembers();
    } catch (err) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await api.post(`/members/join-requests/${requestId}/reject`);
      toast.success('Request rejected');
      fetchJoinRequests();
    } catch (err) {
      toast.error('Failed to reject request');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`📋 ${label} copied!`);
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return <Crown className="w-4 h-4 text-purple-500" />;
      case 'editor': return <Edit className="w-4 h-4 text-blue-500" />;
      default: return <Eye className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleCard = (role) => {
    const configs = {
      admin: { 
        bg: 'bg-gradient-to-br from-purple-100 to-purple-50', 
        border: 'border-purple-200',
        text: 'text-purple-700',
        icon: Crown,
        label: 'Admin'
      },
      editor: { 
        bg: 'bg-gradient-to-br from-blue-100 to-blue-50', 
        border: 'border-blue-200',
        text: 'text-blue-700',
        icon: Edit,
        label: 'Editor'
      },
      viewer: { 
        bg: 'bg-gradient-to-br from-gray-100 to-gray-50', 
        border: 'border-gray-200',
        text: 'text-gray-700',
        icon: Eye,
        label: 'Viewer'
      }
    };
    return configs[role] || configs.viewer;
  };

  const getScopeEmoji = (scope) => scope === 'full' ? '🌐' : '👤';

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="max-w-6xl mx-auto">
        {/* Fun Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl shadow-lg">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Family Access Hub
              </h1>
              <p className="text-gray-500 text-sm sm:text-base">Give your family members their own login - it's like giving them keys to the vault! 🔐</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-emerald-50 to-green-100 rounded-xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              <span className="text-sm text-emerald-700 font-medium">Total Members</span>
            </div>
            <p className="text-2xl font-bold text-emerald-800 mt-1">{members.length}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-blue-700 font-medium">With Access</span>
            </div>
            <p className="text-2xl font-bold text-blue-800 mt-1">{members.filter(m => m.has_login).length}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-yellow-100 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-amber-700 font-medium">Pending</span>
            </div>
            <p className="text-2xl font-bold text-amber-800 mt-1">{members.filter(m => m.invite_status === 'pending').length}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-violet-100 rounded-xl p-4 border border-purple-200">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-purple-700 font-medium">Join Requests</span>
            </div>
            <p className="text-2xl font-bold text-purple-800 mt-1">{joinRequests.length}</p>
          </div>
        </div>

        {/* Join Requests */}
        {joinRequests.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 mb-8 shadow-sm">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2 text-amber-800">
              <PartyPopper className="text-amber-500" />
              New Members Want to Join! 
              <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-sm">{joinRequests.length}</span>
            </h2>
            <div className="space-y-3">
              {joinRequests.map(req => (
                <div key={req.id} className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm border border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                      {req.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{req.name}</p>
                      <p className="text-sm text-gray-500">{req.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      onClick={() => handleApproveRequest(req.id, req.name)}
                    >
                      <Check className="w-4 h-4 mr-1" /> Welcome!
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleRejectRequest(req.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Members Grid */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 sm:p-6 border-b bg-gradient-to-r from-gray-50 to-white">
            <h2 className="font-bold text-lg flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Your Family Team
            </h2>
            <p className="text-sm text-gray-500">Click "Grant Access" to give a member their own login credentials</p>
          </div>
          
          {members.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No family members yet. Add members from the Dashboard first!</p>
            </div>
          ) : (
            <div className="grid gap-4 p-4 sm:p-6 sm:grid-cols-2 lg:grid-cols-3">
              {members.map(member => {
                const roleConfig = getRoleCard(member.role);
                const RoleIcon = roleConfig.icon;
                
                return (
                  <div 
                    key={member.id} 
                    className={`relative rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg ${
                      member.has_login ? 'border-emerald-200 bg-emerald-50/30' : 
                      member.invite_status === 'pending' ? 'border-amber-200 bg-amber-50/30' : 
                      'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Status Badge */}
                    {member.has_login && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 bg-emerald-500 text-white text-xs rounded-full font-medium flex items-center gap-1">
                          <Zap className="w-3 h-3" /> Active
                        </span>
                      </div>
                    )}
                    {member.invite_status === 'pending' && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded-full font-medium animate-pulse flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Pending
                        </span>
                      </div>
                    )}
                    
                    <div className="p-5">
                      {/* Avatar & Name */}
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg ${
                          member.has_login ? 'bg-gradient-to-br from-emerald-400 to-teal-500' :
                          'bg-gradient-to-br from-gray-400 to-gray-500'
                        }`}>
                          {member.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-800 truncate">{member.name}</h3>
                          <p className="text-sm text-gray-500 truncate">{member.relation || 'Family'}</p>
                          {member.email && (
                            <p className="text-xs text-gray-400 truncate mt-0.5">{member.email}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Role & Scope Pills */}
                      {member.has_login && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${roleConfig.bg} ${roleConfig.text} border ${roleConfig.border}`}>
                            <RoleIcon className="w-3 h-3" />
                            {roleConfig.label}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                            member.scope === 'full' 
                              ? 'bg-green-100 text-green-700 border border-green-200' 
                              : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                          }`}>
                            {getScopeEmoji(member.scope)} {member.scope === 'full' ? 'Full Access' : 'Own Only'}
                          </span>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {!member.has_login && !member.invite_status && (
                          <Button
                            className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                            onClick={() => {
                              setSelectedMember(member);
                              setLoginForm({ ...loginForm, email: member.email || '' });
                              setShowCreateLogin(true);
                            }}
                          >
                            <Key className="w-4 h-4 mr-2" />
                            Grant Access
                          </Button>
                        )}
                        {member.has_login && (
                          <>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => {
                                  setSelectedMember(member);
                                  setRoleForm({ role: member.role || 'viewer', scope: member.scope || 'own' });
                                  setShowEditRole(true);
                                }}
                              >
                                <Shield className="w-4 h-4 mr-1" />
                                Permissions
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => {
                                  setSelectedMember(member);
                                  setCredentialsForm({ email: member.email || '', new_password: '' });
                                  setShowEditCredentials(true);
                                }}
                              >
                                <Key className="w-4 h-4 mr-1" />
                                Credentials
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create Login Modal */}
        {showCreateLogin && selectedMember && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
                    <Key className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Grant Access</h3>
                    <p className="text-emerald-100 text-sm">Create login for {selectedMember.name}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">📧 Email Address</label>
                  <input
                    type="email"
                    className="w-full p-3 border-2 rounded-xl focus:border-emerald-500 focus:ring-0 transition-colors"
                    value={loginForm.email}
                    onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                    placeholder="member@email.com"
                  />
                </div>
                
                {/* Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">🔑 Temporary Password</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 p-3 border-2 rounded-xl font-mono text-lg tracking-wider focus:border-emerald-500 focus:ring-0 transition-colors"
                      value={loginForm.temp_password}
                      onChange={e => setLoginForm({ ...loginForm, temp_password: e.target.value })}
                      placeholder="Click generate →"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="px-4 border-2 hover:bg-emerald-50 hover:border-emerald-300"
                      onClick={generatePassword}
                    >
                      <Sparkles className="w-5 h-5 text-emerald-600" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> They'll be asked to change this on first login
                  </p>
                </div>
                
                {/* Role & Scope */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">👑 Role</label>
                    <select
                      className="w-full p-3 border-2 rounded-xl focus:border-emerald-500 focus:ring-0 transition-colors"
                      value={loginForm.role}
                      onChange={e => setLoginForm({ ...loginForm, role: e.target.value })}
                    >
                      <option value="viewer">👁️ Viewer</option>
                      <option value="editor">✏️ Editor</option>
                      <option value="admin">👑 Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">🎯 Scope</label>
                    <select
                      className="w-full p-3 border-2 rounded-xl focus:border-emerald-500 focus:ring-0 transition-colors"
                      value={loginForm.scope}
                      onChange={e => setLoginForm({ ...loginForm, scope: e.target.value })}
                    >
                      <option value="own">👤 Own Only</option>
                      <option value="full">🌐 Full Access</option>
                    </select>
                  </div>
                </div>
                
                {/* Role Explanation */}
                <div className="bg-gray-50 rounded-xl p-4 text-sm">
                  <p className="font-medium text-gray-700 mb-2">Quick Guide:</p>
                  <ul className="space-y-1 text-gray-500">
                    <li>👁️ <strong>Viewer</strong> - Can only see holdings</li>
                    <li>✏️ <strong>Editor</strong> - Can add/edit holdings</li>
                    <li>👑 <strong>Admin</strong> - Full control + manage members</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex gap-3 p-6 bg-gray-50 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowCreateLogin(false); setSelectedMember(null); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  onClick={handleCreateLogin}
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Create Access
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Credentials Display Modal */}
        {showCredentials && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Success Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-white text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur">
                  <PartyPopper className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold">{showCredentials.isReset ? 'Password Reset! 🔑' : 'Access Granted! 🎉'}</h3>
                <p className="text-emerald-100 mt-1">Share these credentials with {showCredentials.name}</p>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Credentials Card */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-5 text-white">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Email</p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-lg">{showCredentials.email}</p>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-white hover:bg-white/10"
                          onClick={() => copyToClipboard(showCredentials.email, 'Email')}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Temporary Password</p>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-xl tracking-wider text-emerald-400">{showCredentials.password}</p>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-white hover:bg-white/10"
                          onClick={() => copyToClipboard(showCredentials.password, 'Password')}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Role Info */}
                <div className="flex gap-2">
                  <span className={`flex-1 text-center py-2 rounded-lg text-sm font-medium ${
                    showCredentials.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                    showCredentials.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {getRoleIcon(showCredentials.role)} {showCredentials.role}
                  </span>
                  <span className={`flex-1 text-center py-2 rounded-lg text-sm font-medium ${
                    showCredentials.scope === 'full' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {getScopeEmoji(showCredentials.scope)} {showCredentials.scope === 'full' ? 'Full Access' : 'Own Only'}
                  </span>
                </div>
                
                {/* Copy All Button */}
                <Button 
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                  onClick={() => {
                    const text = `Maa Dabbu Login Details\n\nEmail: ${showCredentials.email}\nPassword: ${showCredentials.password}\n\nPlease change your password after first login!`;
                    copyToClipboard(text, 'All credentials');
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy All Details
                </Button>
                
                <p className="text-xs text-center text-gray-400">
                  💡 Tip: Share these via a secure channel (WhatsApp, SMS, etc.)
                </p>
              </div>
              
              <div className="p-6 bg-gray-50 border-t">
                <Button 
                  className="w-full"
                  variant="outline"
                  onClick={() => setShowCredentials(null)}
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Role Modal */}
        {showEditRole && selectedMember && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Edit Permissions</h3>
                    <p className="text-purple-100 text-sm">Update access for {selectedMember.name}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-5">
                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Select Role</label>
                  <div className="grid grid-cols-3 gap-3">
                    {['viewer', 'editor', 'admin'].map(role => {
                      const config = getRoleCard(role);
                      const Icon = config.icon;
                      return (
                        <button
                          key={role}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            roleForm.role === role 
                              ? `${config.bg} ${config.border} ring-2 ring-offset-2 ring-${role === 'admin' ? 'purple' : role === 'editor' ? 'blue' : 'gray'}-400`
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setRoleForm({ ...roleForm, role })}
                        >
                          <Icon className={`w-6 h-6 mx-auto mb-2 ${config.text}`} />
                          <p className={`text-sm font-medium ${config.text}`}>{config.label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Scope Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Access Scope</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        roleForm.scope === 'own' 
                          ? 'bg-yellow-50 border-yellow-300 ring-2 ring-offset-2 ring-yellow-400'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setRoleForm({ ...roleForm, scope: 'own' })}
                    >
                      <p className="text-2xl mb-1">👤</p>
                      <p className="font-medium text-gray-800">Own Only</p>
                      <p className="text-xs text-gray-500">See their portfolio only</p>
                    </button>
                    <button
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        roleForm.scope === 'full' 
                          ? 'bg-green-50 border-green-300 ring-2 ring-offset-2 ring-green-400'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setRoleForm({ ...roleForm, scope: 'full' })}
                    >
                      <p className="text-2xl mb-1">🌐</p>
                      <p className="font-medium text-gray-800">Full Access</p>
                      <p className="text-xs text-gray-500">See entire family portfolio</p>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 p-6 bg-gray-50 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowEditRole(false); setSelectedMember(null); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                  onClick={handleUpdateRole}
                >
                  <Check className="w-4 h-4 mr-2" /> Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Credentials Modal */}
        {showEditCredentials && selectedMember && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
                    <Key className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Edit Credentials</h3>
                    <p className="text-blue-100 text-sm">Update login for {selectedMember.name}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-5">
                {/* Current Email Display */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Current Email</p>
                  <p className="font-medium text-gray-800">{selectedMember.email}</p>
                </div>

                {/* New Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">📧 Update Email (optional)</label>
                  <input
                    type="email"
                    className="w-full p-3 border-2 rounded-xl focus:border-blue-500 focus:ring-0 transition-colors"
                    value={credentialsForm.email}
                    onChange={e => setCredentialsForm({ ...credentialsForm, email: e.target.value })}
                    placeholder="Leave empty to keep current"
                  />
                </div>
                
                {/* New Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">🔑 Reset Password (optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 p-3 border-2 rounded-xl font-mono text-lg tracking-wider focus:border-blue-500 focus:ring-0 transition-colors"
                      value={credentialsForm.new_password}
                      onChange={e => setCredentialsForm({ ...credentialsForm, new_password: e.target.value })}
                      placeholder="Click generate →"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="px-4 border-2 hover:bg-blue-50 hover:border-blue-300"
                      onClick={() => {
                        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
                        let password = '';
                        for (let i = 0; i < 12; i++) {
                          password += chars.charAt(Math.floor(Math.random() * chars.length));
                        }
                        setCredentialsForm({ ...credentialsForm, new_password: password });
                        toast.success('🎲 Password generated!');
                      }}
                    >
                      <Sparkles className="w-5 h-5 text-blue-600" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Member will be asked to change on next login
                  </p>
                </div>

                {/* Revoke Access Section */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-500 mb-2">Danger Zone</p>
                  <Button 
                    variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                    onClick={handleRevokeAccess}
                  >
                    <X className="w-4 h-4 mr-2" /> Revoke Access
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-3 p-6 bg-gray-50 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowEditCredentials(false); setSelectedMember(null); setCredentialsForm({ email: '', new_password: '' }); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  onClick={handleUpdateCredentials}
                >
                  <Check className="w-4 h-4 mr-2" /> Update Credentials
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MemberAccess;
